import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import "./login.css";
import Button from "../../components/Button";
import TextField from "../../components/TextField";

import eyeIcon from "../../assets/icons/eye.svg";
import eyeOffIcon from "../../assets/icons/eye-off.svg";
import logoImage from "../../assets/Capturas-cotos-de-caza-1.png";
import footerLogos from "../../assets/logos/logos_footter.png";
import footerLogos_2 from "../../assets/logos/logo_footer_retech.png";

export default function Login() {
    const navigate = useNavigate();
    const { login, user, loading } = useAuth();
    const [data, setData] = useState({
        email: "",
        password: "",
        remember: false,
    });
    const [processing, setProcessing] = useState(false);
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    // Si el usuario ya está autenticado, redirigir
    useEffect(() => {
        if (!loading && user) {
            navigate("/general");
        }
    }, [user, loading, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);
        setErrors({});
        setError(null);
        
        try {
            await login(data.email, data.password, data.remember);
            // Redirect to dashboard on success
            navigate("/general");
        } catch (err) {
            Sentry.captureException(new Error('Login error: ' + err.message));
            if (err.response?.data?.errors) {
                setErrors(err.response.data.errors);
            }
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Error al iniciar sesión');
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleChange = (field, value) => {
        setData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="login-page">
                {/* Background decorative shapes */}
                <div className="login-background-shape login-background-shape--top-right"></div>
                <div className="login-background-shape login-background-shape--bottom-right"></div>

                {/* Main login card */}
                <div className="login-card">
                    {/* Logo */}
                    <div className="login-logo">
                        <div className="login-logo-image">
                            <img
                                src={logoImage}
                                alt="Logo"
                                className="login-logo-img"
                            />
                        </div>
                    </div>

                    <p className="login-metrics-title">
                        Panel de datos del Sistema de control telemático de capturas en los cotos de caza de Castilla y León
                    </p>

                    {/* Heading */}
                    <h2 className="login-heading">Inicia sesión</h2>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="login-form">
                        {/* Error message from session flash */}
                        {error && (
                            <div
                                style={{
                                    backgroundColor: "#fee",
                                    color: "#c33",
                                    padding: "12px 16px",
                                    borderRadius: "8px",
                                    marginBottom: "16px",
                                    border: "1px solid #fcc",
                                    fontSize: "12px",
                                }}
                            >
                                {error}
                            </div>
                        )}
                        {/* Email field */}
                        <div className="login-field">
                            <label htmlFor="email" className="login-label">
                                Correo electrónico
                            </label>
                            <input
                                id="email"
                                type="email"
                                className={`text-field ${errors.email ? "text-field--error" : ""}`}
                                placeholder="tucorreo@correo.com"
                                value={data.email}
                                onChange={(e) =>
                                    handleChange("email", e.target.value)
                                }
                                required
                                disabled={processing}
                            />
                            {errors.email && (
                                <span className="form-error">
                                    {errors.email}
                                </span>
                            )}
                        </div>

                        {/* Password field */}
                        <div className="login-field">
                            <label htmlFor="password" className="login-label">
                                Contraseña
                            </label>
                            <TextField
                                id="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="Contraseña"
                                value={data.password}
                                onChange={(e) =>
                                    handleChange("password", e.target.value)
                                }
                                withPasswordToggle={true}
                                showPassword={showPassword}
                                onTogglePassword={() =>
                                    setShowPassword(!showPassword)
                                }
                                passwordToggleIcon={
                                    <img
                                        src={
                                            showPassword ? eyeOffIcon : eyeIcon
                                        }
                                        alt=""
                                    />
                                }
                                required
                            />
                            {errors.password && (
                                <span className="form-error">
                                    {errors.password}
                                </span>
                            )}
                        </div>

                        {/* Remember password checkbox */}
                        <label className="login-checkbox-label">
                            <input
                                type="checkbox"
                                className="login-checkbox"
                                checked={data.remember}
                                onChange={(e) =>
                                    handleChange("remember", e.target.checked)
                                }
                                disabled={processing}
                            />
                            <span>Recordar contraseña</span>
                        </label>

                        {/* Submit button */}
                        <Button
                            type="submit"
                            variant="accent"
                            disabled={processing}
                            className={`login-submit-button ${processing ? "login-submit-button--loading" : ""}`}
                        >
                            {processing
                                ? "Iniciando sesión..."
                                : "Iniciar sesión"}
                        </Button>

                        {/* Forgot password link */}
                        <p className="login-forgot-password">
                            ¿Has olvidado tu contraseña?{" "}
                            <a href="https://capturascotos.cazacyl.es/login/forgot-password" className="login-link">
                                Recuperar contraseña
                            </a>
                        </p>
                    </form>
                </div>

                {/* Footer */}
                <footer className="login-footer">
                    <div className="login-footer-logos">
                        <img
                            src={footerLogos}
                            alt="Logos"
                            className="login-footer-logos-img"
                            width="20%"
                        />
                        <img
                            src={footerLogos_2}
                            alt="Logos"
                            className="login-footer-logos-img"
                            width="70%"
                        />
                    </div>
                    <p className="login-footer-copyright">
                        {new Date().getFullYear()}. Servicio ofrecido por{" "}
                        <a href="#" className="login-link">
                            Cesefor
                        </a>
                    </p>
                    <p className="login-footer-support">
                        Si necesitas soporte contacta con:{" "}
                        <a
                            href="mailto:soporte@capturascotos.app"
                            className="login-link"
                        >
                            soporte@capturascotos.app
                        </a>
                    </p>
                </footer>
            </div>
    );
}
